/**
 * Client-side Work follow-up queue.
 *
 * While a task is running, Enter / Queue waits until the current run
 * finishes. Send now steers immediately. Queued items render only in the
 * WorkFollowUpQueue card until they are sent.
 * Pure helpers so the store and tests share one shape.
 */

export const WORK_FOLLOWUP_QUEUE_MAX = 20;

// Enter does not set a sender, so a live Work composer defaults to queue.
// Only the Send now control opts into an immediate steer.
export function resolveWorkComposerSender(requested, { workFollowUpMode = false } = {}) {
  if (requested === 'now') return 'now';
  if (requested === 'queue') return 'queue';
  return workFollowUpMode ? 'queue' : 'now';
}

// Local transcript ids for queued follow-ups. They render as user bubbles
// with isQueued until Send now / auto-flush turns them into a real send.
export const WORK_FOLLOWUP_MESSAGE_ID_PREFIX = 'msg_q_';

export function workFollowUpMessageId(followUpId) {
  const id = String(followUpId || '').trim();
  if (!id) return '';
  return `${WORK_FOLLOWUP_MESSAGE_ID_PREFIX}${id}`;
}

export function isWorkFollowUpQueueMessage(message) {
  const id = typeof message === 'string' ? message : message?.id;
  return typeof id === 'string' && id.startsWith(WORK_FOLLOWUP_MESSAGE_ID_PREFIX);
}

export function workFollowUpChatMessage(item, files = []) {
  if (!item?.id) return null;
  return {
    id: workFollowUpMessageId(item.id),
    content: String(item.text || ''),
    files: Array.isArray(files) ? files : [],
    role: 'user',
    timestamp: new Date(item.createdAt || Date.now()).toISOString(),
    isQueued: true,
    isPending: false,
  };
}

export function createWorkFollowUp({ text, fileIds } = {}) {
  const trimmed = String(text || '').trim();
  const files = Array.isArray(fileIds) ? fileIds.map(String).filter(Boolean) : [];
  if (!trimmed && !files.length) return null;
  return {
    id: `fu_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    text: trimmed,
    fileIds: files,
    createdAt: Date.now(),
  };
}

export function normalizeWorkFollowUp(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const text = String(entry.text || '').trim();
  const fileIds = Array.isArray(entry.fileIds) ? entry.fileIds.map(String).filter(Boolean) : [];
  if (!text && !fileIds.length) return null;
  const id = String(entry.id || '').trim();
  if (!id) return null;
  return {
    id,
    text,
    fileIds,
    createdAt: Number(entry.createdAt) || Date.now(),
  };
}

export function normalizeWorkFollowUpQueue(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const item = normalizeWorkFollowUp(entry);
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
    if (out.length >= WORK_FOLLOWUP_QUEUE_MAX) break;
  }
  return out;
}

export function enqueueWorkFollowUp(queue, item, { front = false } = {}) {
  const nextItem = item?.id ? normalizeWorkFollowUp(item) : createWorkFollowUp(item);
  if (!nextItem) return Array.isArray(queue) ? queue : [];
  const current = normalizeWorkFollowUpQueue(queue).filter((entry) => entry.id !== nextItem.id);
  const next = front ? [nextItem, ...current] : [...current, nextItem];
  return next.slice(0, WORK_FOLLOWUP_QUEUE_MAX);
}

export function updateWorkFollowUp(queue, id, patch) {
  const targetId = String(id || '');
  if (!targetId) return Array.isArray(queue) ? queue : [];
  return normalizeWorkFollowUpQueue(queue).map((entry) => {
    if (entry.id !== targetId) return entry;
    const text = patch?.text != null ? String(patch.text).trim() : entry.text;
    const fileIds = patch?.fileIds != null
      ? (Array.isArray(patch.fileIds) ? patch.fileIds.map(String).filter(Boolean) : [])
      : entry.fileIds;
    if (!text && !fileIds.length) return entry;
    return { ...entry, text, fileIds };
  }).filter(Boolean);
}

export function removeWorkFollowUp(queue, id) {
  const targetId = String(id || '');
  return normalizeWorkFollowUpQueue(queue).filter((entry) => entry.id !== targetId);
}

export function dequeueWorkFollowUp(queue) {
  const current = normalizeWorkFollowUpQueue(queue);
  if (!current.length) return { next: null, rest: [] };
  const [next, ...rest] = current;
  return { next, rest };
}

export function reorderWorkFollowUp(queue, fromIndex, toIndex) {
  const current = normalizeWorkFollowUpQueue(queue);
  const from = Number(fromIndex);
  const to = Number(toIndex);
  if (!Number.isInteger(from) || !Number.isInteger(to)) return current;
  if (from < 0 || from >= current.length) return current;
  if (to < 0 || to >= current.length) return current;
  if (from === to) return current;
  const next = current.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function workFollowUpQueueCountLabel(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n === 1) return '1 queued follow-up';
  return `${n} queued follow-ups`;
}
